import React from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Stack,
  Tooltip,
} from "@mui/material";
import { NavigateBefore, NavigateNext, Send } from "@mui/icons-material";
import { StandardTableActions } from "../../../components/StandardTableActions/StandardTableActions";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { useDispatchWrapper } from "../../../hooks";
import { IQuickReplyTemplateData } from "../../../redux/quickReplyTemplates/quickReplyTemplate.types";
import { upsertQuickReplyTemplate } from "../../../redux/quickReplyTemplates/quickReplyTemplateAction";
import { formatText } from "../../../helpers";
import { ManageQuickReplyTemplateRightPanel } from "../ManageQuickReplyTemplateRightPanel";
import { showMessage } from "../../../redux/messages/messagesActions";
import { ViewQuickReplyTemplateDetails } from "./ViewQuickReplyTemplateDetails";

interface IQuickTemplateTableProps {
  fetchList?: () => void;
  templates: IQuickReplyTemplateData[];
  selectedTemplate: string | null;
  onWhatsappMessageSend: (template: IQuickReplyTemplateData) => void;
  toNumber?: string;
  // Old cursor-based pagination props (kept for backward compatibility)
  paging?: {
    cursors: { before: string; after: string };
    next: string;
    itemPerPage: number;
  };
  // New traditional pagination props
  currentPage?: number;
  totalRecords?: number;
  onPageChange: (
    direction?: "next" | "prev",
    cursor?: string,
    rowsPerPage?: number
  ) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPage: number;
  searchTemplateName?: string;
  onTemplatePreview: (template: IQuickReplyTemplateData) => void;
  isFromQuickReply?: boolean;
  isFromChat?: boolean;
}

export const QuickTemplateTable: React.FC<IQuickTemplateTableProps> = ({
  fetchList,
  templates,
  selectedTemplate,
  onWhatsappMessageSend,
  toNumber,
  paging,
  currentPage = 1,
  totalRecords = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPage,
  searchTemplateName,
  onTemplatePreview,
  isFromQuickReply = false,
  isFromChat = false,
}) => {
  const dispatch = useDispatchWrapper();

  const [openView, setOpenView] =
    React.useState<IQuickReplyTemplateData | null>(null);

  // Filter templates based on search (for local filtering if needed)
  const filteredTemplates = templates.filter((template) => {
    if (!searchTemplateName) return true;
    const searchTerms = searchTemplateName.toLowerCase().trim().split(/\s+/);
    const templateName = template.name.toLowerCase().trim();
    return searchTerms.every((term) => templateName.includes(term));
  });

  // Calculate pagination - API already returns paginated data, no need to slice
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const isBackDisabled = currentPage <= 1;
  const isNextDisabled =
    currentPage >= totalPages || filteredTemplates.length < rowsPerPage;

  const [openEditView, setOpenEditView] =
    React.useState<IQuickReplyTemplateData | null>(null);

  const handleDeleteTemplate = (template: IQuickReplyTemplateData) => {
    const updatedValues: IQuickReplyTemplateData = {
      ...template,
      status: "INACTIVE",
    };

    dispatch(
      upsertQuickReplyTemplate(
        updatedValues as IQuickReplyTemplateData,
        (isSuccess, templateData) => {
          if (isSuccess) {
            fetchList?.();
          }
        }
      )
    );
  };
  const handleReactive = (row: IQuickReplyTemplateData) => {
    if (!row.name) return;

    const reactiveStatus = isFromQuickReply ? "ACTIVE" : "APPROVED";

    dispatch(
      upsertQuickReplyTemplate(
        {
          ...row,
          status: reactiveStatus,
        },
        (isSuccess?: boolean) => {
          if (isSuccess) {
            fetchList?.();
            dispatch(
              showMessage({
                type: "success",
                message: "Template reactivated successfully",
                displayAs: "snackbar",
              })
            );
          } else {
            dispatch(
              showMessage({
                type: "error",
                message: "Failed to reactivate template",
                displayAs: "snackbar",
              })
            );
          }
        }
      )
    );
  };

  const handleEditTemplate = (template: IQuickReplyTemplateData) => {
    setOpenEditView(template);
  };

  return (
    <>
      <TableContainer sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              {isFromChat && (
                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                  }}
                >
                  Send
                </TableCell>
              )}

              <TableCell
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  minWidth: "100px",
                }}
              >
                Actions
              </TableCell>
              <TableCell
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  minWidth: "200px",
                }}
              >
                Template Name
              </TableCell>
              <TableCell
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  minWidth: "200px",
                }}
              >
                Template Text
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates?.map((template, index) => {
              const isApproved = isFromQuickReply
                ? template.status === "ACTIVE"
                : template.status === "APPROVED";
              return (
                <TableRow
                  key={index}
                  hover
                  onClick={() => onTemplatePreview(template)}
                  sx={{ cursor: "pointer" }}
                >
                  {isFromChat && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Send WhatsApp Message">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isApproved && toNumber) {
                              onWhatsappMessageSend(template);
                            }
                          }}
                          disabled={!isApproved || !toNumber}
                          sx={{
                            rotate: "-30deg",
                            color:
                              isApproved && toNumber
                                ? "primary.main"
                                : "action.disabled",
                          }}
                        >
                          <Send />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <StatusRenderer status={template.status || ""} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <StandardTableActions
                        status={template.status ?? undefined}
                      onViewClick={() => setOpenView(template)}
                      onEditClick={() => handleEditTemplate(template)}
                      onDeleteClick={() => handleDeleteTemplate(template)}
                      // onReactiveClick={() => handleReactive(template)}
                    />
                  </TableCell>
                  <TableCell>{formatText(template.name)}</TableCell>
                  <TableCell>{template.text}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" alignItems="center" p={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  const newRowsPerPage = e.target.value as number;
                  onRowsPerPageChange(newRowsPerPage);
                  onPageChange(undefined, undefined, newRowsPerPage);
                }}
                sx={{ color: "text.secondary" }}
              >
                <MenuItem value={9}>9</MenuItem>
                <MenuItem value={18}>18</MenuItem>
                <MenuItem value={36}>36</MenuItem>
              </Select>
            </FormControl>

            <IconButton
              onClick={() => onPageChange("prev")}
              disabled={isBackDisabled}
            >
              <NavigateBefore />
              <Typography variant="body2">Back</Typography>
            </IconButton>

            <IconButton
              onClick={() => onPageChange("next")}
              disabled={isNextDisabled}
            >
              <Typography variant="body2">Next</Typography>
              <NavigateNext />
            </IconButton>
          </Stack>
        </Box>
      </TableContainer>
      {openView && (
        <ViewQuickReplyTemplateDetails
          type={"TEMPLATE_LIST"}
          open={true}
          onClose={() => setOpenView(null)}
          quickReplyTemplateData={openView}
        />
      )}

      {openEditView && (
        <ManageQuickReplyTemplateRightPanel
          open
          templateData={openEditView}
          onClose={() => setOpenEditView(null)}
          onSaveSuccess={() => {
            setOpenEditView(null);
            fetchList?.();
          }}
        />
      )}
    </>
  );
};
